import { formatTimeOfDay, isoAttr } from "../../lib/datetime.ts";
import { useMemo, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ScreenScaffold, type ScreenState } from "../ScreenScaffold.tsx";
import { useIdentity, roleAtLeast } from "../../state/identity.tsx";
import { useTimeRange } from "../../state/time-range.tsx";
import { LivePauseControl } from "../../chrome/LivePauseControl.tsx";
import { TimeRangeControl } from "../../chrome/TimeRangeControl.tsx";
import { useStreamView } from "../../state/stream-view.tsx";
import { formatMeasured } from "../../components/index.ts";
import { useEvents, type EventsWindow } from "./use-events.ts";
import {
  countMeasure,
  eventMatchesChips,
  isEventsEmpty,
  readFilters,
  type EventFilters,
  type EventPage,
  type EventRow,
} from "./vocab.ts";
import { FilterBar } from "./FilterBar.tsx";
import { TailStatus } from "./TailStatus.tsx";
import { EventsTable } from "./EventsTable.tsx";
import {
  DashboardGrid,
  DashboardGridFull,
  ChartCard,
} from "../../components/observability/index.ts";
import type { ChartData, ChartSeries } from "../../components/Chart.tsx";
import "./events.css";

/**
 * The Events explorer (task 11.12; design 12.2 Events row, 12.3 principles, 8.2 live-tail pause).
 * The KEYSTONE screen: every other screen drills INTO it, and it consumes the 11.3 live-stream
 * client's pause / buffer / overflow mechanics (which this screen READS, never reimplements).
 *
 * What it does:
 * - LIVE TAIL: while live, the primary surface is the stream view's events RING (`useStreamView`),
 *   newest-first, filtered client-side by the drill chips the URL carries. New events appear as the
 *   always-on stream driver folds deltas in. When PAUSED, the client parks deltas and this screen
 *   shows the frozen tail plus the buffered count (via {@link TailStatus}); ordered replay on resume
 *   is automatic inside the client.
 * - PAGED / FROZEN view: the windowed `GET /events` read backs the scrolled-back view and, crucially,
 *   supplies the SAMPLE-RATE-AWARE `weighted_count` and the `meta.data_quality` the screen reads to
 *   label sampling honestly ("est." when sampling is in effect; design 12.3).
 * - STANDING FILTERS + drill params: the URL is the single source of truth. {@link readFilters}
 *   resolves the ten named standing filters (sent to the server) and the other screens' drill params
 *   (applied client-side as visible, clearable chips). {@link FilterBar} renders them.
 * - The four screen states and respect for the pause/time-range.
 *
 * The live tail and the paged page are shown together: the paged header carries the window + count +
 * sampling, and the table below is the live ring while live, or the paged items while frozen (so a
 * paused operator studies the exact window the count describes).
 *
 * Observability-ui Stage 5 (task 5.1; design §Investigate): the ready body composes the
 * observability kit over the SAME surfaces it already reads — this stage is PRESENTATION-ONLY, so
 * the live ring, the paged read and the client-side chip filtering are untouched. A `DashboardGrid`
 * lays out a `ChartCard` of the event volume per time bucket (a real time dimension — each shown
 * row's `ts` bucketed across the window; honest empty until there are two buckets, never a
 * fabricated line, design Property 2) above the existing semantic `EventsTable`. The chart draws
 * from exactly the rows the table shows (the live ring while live, the paged items while frozen), so
 * the two always agree. Every surface derives its colours from the theme tokens via the kit's CSS,
 * so the screen reads correctly in both light and dark (design Property 1).
 */
export function EventsScreen(): ReactNode {
  const { role } = useIdentity();
  const { live } = useTimeRange();
  const [params] = useSearchParams();

  // The URL is the source of truth for filters; re-read on every param change.
  const filters = useMemo<EventFilters>(() => readFilters(params), [params]);

  // The live view ring (the 11.3 client's events). Filtered client-side by the drill chips, then
  // shown newest-first for the tail.
  const { view } = useStreamView();
  const liveEvents = useMemo<readonly EventRow[]>(() => {
    const matched = view.events.filter((e) => eventMatchesChips(e, filters.chips));
    // The ring is oldest-first; the tail shows newest-first.
    return [...matched].reverse();
  }, [view.events, filters.chips]);

  const canRead = roleAtLeast(role, "viewer");

  // The paged read backs the frozen view and the honest count; only run it for a permitted role.
  const { query, window } = useEvents({ standing: filters.standing, enabled: canRead });

  const pagedEvents = useMemo<readonly EventRow[]>(() => {
    if (query.data === undefined) return [];
    return query.data.items.filter((e) => eventMatchesChips(e, filters.chips));
  }, [query.data, filters.chips]);

  const state = resolveState(role, live, liveEvents.length, query, pagedEvents.length);

  return (
    <ScreenScaffold
      title="Events"
      subtitle="Filterable, live-tail explorer over redacted events with standing filters and sample-rate-aware counts. Pausing freezes the tail and buffers new events; a gap after overflow is disclosed."
      state={state}
    >
      <div className="evt" data-live={live}>
        <FilterBar filters={filters} />
        <TailStatus live={live} />
        {query.data !== undefined && (
          <EventsBody
            live={live}
            window={window}
            page={query.data}
            liveEvents={liveEvents}
            pagedEvents={pagedEvents}
            hasChips={filters.chips.length > 0}
          />
        )}
      </div>
    </ScreenScaffold>
  );
}

/** Decide which scaffold state to show from role, the live tail and the paged query. */
function resolveState(
  role: ReturnType<typeof useIdentity>["role"],
  live: boolean,
  liveCount: number,
  query: ReturnType<typeof useEvents>["query"],
  pagedCount: number,
): ScreenState {
  // Least privilege: an unknown or below-viewer role sees the restricted state, not an error.
  if (!roleAtLeast(role, "viewer")) {
    return {
      kind: "restricted",
      reason:
        role === "unknown"
          ? "Your role has not been resolved yet, so the events explorer is hidden until it is. The server enforces access regardless."
          : "The events explorer needs at least the viewer role.",
    };
  }

  if (query.data !== undefined) {
    // The paged read supplies the window header and count even when the live tail is the surface.
    if (isEventsEmpty(liveCount, pagedCount)) {
      return {
        kind: "empty",
        cause: live
          ? "No events have arrived on the live tail and none matched in this window. Widen the time range, loosen the filters, or check that the services are reporting."
          : "No events matched the filters in this window. Widen the time range or loosen the filters.",
      };
    }
    return { kind: "ready" };
  }

  if (query.isError) {
    // The cold-core waking error is the shell's banner; keep "loading" under it (no contradiction).
    if (query.error.isWaking) return { kind: "loading" };
    return { kind: "error", message: errorMessage(query.error) };
  }

  return { kind: "loading" };
}

function errorMessage(error: ReturnType<typeof useEvents>["query"]["error"]): string {
  if (error === null) return "The events explorer could not load.";
  switch (error.kind) {
    case "unauthorized":
      return "Your session has expired. Reload to sign in again.";
    case "forbidden":
      return "You are not permitted to see events. The server enforces access.";
    case "network":
      return "The events request could not be sent. Check your connection.";
    case "request":
      // A 400 here most often means a hand-edited URL carried an invalid filter value.
      return `The events request was rejected: ${error.message}`;
    default:
      return error.message;
  }
}

function EventsBody(props: {
  live: boolean;
  window: EventsWindow;
  page: EventPage;
  liveEvents: readonly EventRow[];
  pagedEvents: readonly EventRow[];
  hasChips: boolean;
}): ReactNode {
  const { page, window } = props;
  const generatedAt = page.meta.generated_at;
  const sampling = page.meta.data_quality.sampling_in_effect;
  const count = page.weighted_count;
  const measure = countMeasure(count);

  // The surface: the live ring while live, the paged items while frozen (so a paused operator studies
  // the exact window the count describes).
  const rows = props.live ? props.liveEvents : props.pagedEvents;
  const caption = props.live
    ? "Live tail of redacted events, newest first. Open an event for its detail; a trace link opens its waterfall."
    : "Redacted events in the frozen window, newest first. Open an event for its detail; a trace link opens its waterfall.";

  // The event volume over time, bucketed from exactly the rows shown below (so the chart and the
  // table always agree). Honest empty until there are two buckets — never a fabricated line.
  const volume = eventVolumeChart(rows, window);

  return (
    <>
      <div className="screen-meta-row">
        <LivePauseControl />
        <TimeRangeControl />
        <p className="evt__meta">
        {props.live ? "Live" : "Paused"} · window{" "}
        <time dateTime={isoAttr(window.fromMs)}>
          {formatTimeOfDay(window.fromMs)}
        </time>{" "}
        to{" "}
        <time dateTime={isoAttr(window.toMs)}>
          {formatTimeOfDay(window.toMs)}
        </time>{" "}
        · generated{" "}
        <time dateTime={isoAttr(generatedAt)}>
          {formatTimeOfDay(generatedAt)}
        </time>
        {" · "}
        <span className="evt__count" title={`basis: ${count.basis ?? "unknown"}`}>
          {count.value === null
            ? "count unavailable"
            : `${formatMeasured({ value: count.value, measure })} matching`}
        </span>
        {sampling && (
          <span className="evt__meta-flag" role="note">
            {" "}
            · sampling in effect. Counts are weighted estimates and shown as “est.”
          </span>
        )}
        {props.hasChips && (
          <span className="evt__meta-flag" role="note">
            {" "}
            · some filters are applied in your browser (see the bar above) and do not change the
            server count
          </span>
        )}
      </p>
      </div>

      <DashboardGrid label="Events dashboard">
        <ChartCard
          title={volume.title}
          unit={volume.unit}
          data={volume.data}
          series={volume.series}
          {...(volume.empty !== undefined ? { empty: volume.empty } : {})}
        />

        {/* The existing semantic events table, kept as the detailed surface inside a kit panel
            (design §Investigate: StatusGrid/table + ChartCards). It still owns the drill to each
            event's detail and to a trace's waterfall. */}
        <DashboardGridFull>
          <section className="obs-panel evt-table-card" aria-label="Events table">
            <EventsTable
              events={rows}
              caption={caption}
              emptyMessage={
                props.live
                  ? "No events on the live tail match the current filters yet."
                  : "No events matched the filters in this window."
              }
            />
          </section>
        </DashboardGridFull>
      </DashboardGrid>
    </>
  );
}

/** An event-volume chart card plus (when there is no data) an honest cause. */
interface EventVolumeChart {
  readonly title: string;
  readonly unit: string;
  readonly data: ChartData;
  readonly series: readonly ChartSeries[];
  readonly empty?: string;
}

/** The single accent-stroked series the volume chart draws; theme-resolved by the Chart wrapper. */
const EVENT_VOLUME_SERIES: readonly ChartSeries[] = [
  { label: "events", colorVar: "--accent", width: 1.5 },
];

/**
 * The number of equal time buckets the window is split into for the event-volume chart. A fixed
 * count keeps the x axis stable as the window slides, so the chart reads as a steady trend rather
 * than jumping its bucket width.
 */
const EVENT_VOLUME_BUCKETS = 24;

/**
 * Build the event-volume ChartCard data from exactly the rows shown in the table (the live ring
 * while live, the paged items while frozen): split the resolved window into a fixed number of equal
 * time buckets and count how many events fell in each (by `ts`). This is real data off the surface
 * the screen already renders — nothing is fetched or fabricated. When fewer than two buckets carry
 * any event, the card is handed an empty dataset and an honest cause so the Chart wrapper shows its
 * empty state rather than a flat fabricated line (design Property 2).
 */
function eventVolumeChart(
  rows: readonly EventRow[],
  window: EventsWindow,
): EventVolumeChart {
  const base = { title: "Event volume", unit: "per bucket", series: EVENT_VOLUME_SERIES };
  const span = window.toMs - window.fromMs;
  if (span <= 0 || rows.length === 0) {
    return { ...base, data: [[], []], empty: "No events in this window." };
  }

  const width = span / EVENT_VOLUME_BUCKETS;
  const counts = new Array<number>(EVENT_VOLUME_BUCKETS).fill(0);
  let placed = 0;
  for (const e of rows) {
    const at = e.ts;
    if (!Number.isFinite(at) || at < window.fromMs || at > window.toMs) continue;
    const idx = Math.min(
      EVENT_VOLUME_BUCKETS - 1,
      Math.max(0, Math.floor((at - window.fromMs) / width)),
    );
    counts[idx] = (counts[idx] ?? 0) + 1;
    placed += 1;
  }

  const nonEmpty = counts.filter((c) => c > 0).length;
  if (placed === 0 || nonEmpty < 2) {
    return { ...base, data: [[], []], empty: "Not enough events in this window to chart a trend." };
  }

  // Bucket start times (Unix ms) on the x axis, so the Chart wrapper's default `time` axis renders
  // them as clock time — the volume is drawn against when events happened, not an index.
  const xs = counts.map((_, i) => Math.round(window.fromMs + i * width));
  return { ...base, data: [xs, counts] };
}
